import { Box3, Vector3 } from "three";

import type { MuseumContent } from "./contracts";
import type { MuseumScene } from "./museum-scene";

type MapPoint = { artworkId: string; x: number; y: number };

const PADDING = 28;

export class MuseumMiniMap {
  readonly #canvas: HTMLCanvasElement;
  readonly #context: CanvasRenderingContext2D;
  readonly #museum: MuseumScene;
  readonly #content: MuseumContent;
  readonly #bounds: Box3;
  readonly #onNavigate: (artworkId: string) => void;
  #points: MapPoint[] = [];

  constructor(
    canvas: HTMLCanvasElement,
    museum: MuseumScene,
    content: MuseumContent,
    onNavigate: (artworkId: string) => void,
  ) {
    this.#canvas = canvas;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("MINIMAP_CANVAS_UNAVAILABLE");
    this.#context = context;
    this.#museum = museum;
    this.#content = content;
    this.#onNavigate = onNavigate;
    this.#bounds = new Box3().makeEmpty();
    for (const room of museum.roomFloors.values()) this.#bounds.union(room);
    if (museum.corridorFloor !== null) this.#bounds.union(museum.corridorFloor);
    canvas.addEventListener("click", (event) => this.#click(event));
  }

  render(activeArtworkId: string): void {
    const context = this.#context;
    context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
    context.fillStyle = "#121717";
    context.fillRect(0, 0, this.#canvas.width, this.#canvas.height);

    for (const [roomId, bounds] of this.#museum.roomFloors) {
      this.#drawFloor(bounds, roomId === this.#content.entrance_room_id ? "#323b39" : "#2b3332");
    }
    if (this.#museum.corridorFloor !== null) {
      this.#drawFloor(this.#museum.corridorFloor, "#3b3a32");
    }

    this.#points = [];
    for (const artwork of this.#content.artworks) {
      const painting = this.#museum.paintings.get(artwork.id);
      if (painting === undefined) continue;
      const point = this.#project(painting.getWorldPosition(new Vector3()));
      this.#points.push({ artworkId: artwork.id, ...point });
      const active = artwork.id === activeArtworkId;
      context.beginPath();
      context.arc(point.x, point.y, active ? 10 : 7, 0, Math.PI * 2);
      context.fillStyle = active ? "#f4d98e" : "#d4b978";
      context.fill();
      context.fillStyle = "#171b19";
      context.font = `700 ${active ? 14 : 11}px Inter, Arial, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(String(artwork.route_order), point.x, point.y + 0.5);
    }

    this.#drawVisitor();
  }

  #drawFloor(bounds: Box3, fill: string): void {
    const topLeft = this.#project(new Vector3(bounds.min.x, 0, bounds.max.z));
    const bottomRight = this.#project(new Vector3(bounds.max.x, 0, bounds.min.z));
    const context = this.#context;
    context.fillStyle = fill;
    context.strokeStyle = "#7e8883";
    context.lineWidth = 3;
    context.fillRect(
      topLeft.x,
      topLeft.y,
      bottomRight.x - topLeft.x,
      bottomRight.y - topLeft.y,
    );
    context.strokeRect(
      topLeft.x,
      topLeft.y,
      bottomRight.x - topLeft.x,
      bottomRight.y - topLeft.y,
    );
  }

  #drawVisitor(): void {
    const position = this.#project(this.#museum.camera.position);
    const forward = new Vector3(0, 0, -1).applyQuaternion(this.#museum.camera.quaternion);
    const directionX = forward.x;
    const directionY = -forward.z;
    const rightX = -directionY;
    const rightY = directionX;
    const context = this.#context;
    context.beginPath();
    context.moveTo(position.x + directionX * 15, position.y + directionY * 15);
    context.lineTo(
      position.x - directionX * 8 + rightX * 7,
      position.y - directionY * 8 + rightY * 7,
    );
    context.lineTo(
      position.x - directionX * 8 - rightX * 7,
      position.y - directionY * 8 - rightY * 7,
    );
    context.closePath();
    context.fillStyle = "#f5f1e8";
    context.fill();
    context.strokeStyle = "#171d1d";
    context.lineWidth = 2;
    context.stroke();
  }

  #project(position: Vector3): { x: number; y: number } {
    const usableWidth = this.#canvas.width - PADDING * 2;
    const usableHeight = this.#canvas.height - PADDING * 2;
    const width = Math.max(this.#bounds.max.x - this.#bounds.min.x, 0.001);
    const depth = Math.max(this.#bounds.max.z - this.#bounds.min.z, 0.001);
    const scale = Math.min(usableWidth / width, usableHeight / depth);
    const drawnWidth = width * scale;
    const drawnHeight = depth * scale;
    return {
      x: (this.#canvas.width - drawnWidth) / 2 + (position.x - this.#bounds.min.x) * scale,
      y: (this.#canvas.height - drawnHeight) / 2 + (this.#bounds.max.z - position.z) * scale,
    };
  }

  #click(event: MouseEvent): void {
    const bounds = this.#canvas.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * this.#canvas.width;
    const y = ((event.clientY - bounds.top) / bounds.height) * this.#canvas.height;
    const nearest = this.#points
      .map((point) => ({ point, distance: Math.hypot(point.x - x, point.y - y) }))
      .sort((left, right) => left.distance - right.distance)[0];
    if (nearest !== undefined && nearest.distance <= 24) {
      this.#onNavigate(nearest.point.artworkId);
    }
  }
}
